/*
 *
 * DocumentsStatementsCustomDownloadModal
 *
 * @version 1.0.0
 * @author Patrizia Petrera
 * @since 09-Apr-2025
 */

import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { DocumentPDF, DocumentXLS } from '@nexidigital/iconlibrary'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import MerchantModal from '@nexidigital/merchantmodal'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import PortalRadioBox from '@nexidigital/portalradiobox'
import { parseISPStatementFiles, getMultipleDownloadFileStatementName } from 'Utility/globalMethods'

import './DocumentsStatementsCustomDownloadModal.scss'

const DocumentsStatementsCustomDownloadModal = (props) => {
  const intl = useIntl()
  const initialDownloadType = '' // Default to empty - 'A' for Analytical, 'S' for Synthetic
  const [downloadType, setDownloadType] = useState(initialDownloadType)
  const [activeDownloadFormat, setActiveDownloadFormat] = useState(null) // Track which button was clicked
  const [requestParams, setRequestParams] = useState(props.requestParams)
  const files = props.requestParams && props.requestParams.documentId ? parseISPStatementFiles(props.requestParams.documentId) : {}

  useEffect(() => {
    if (props.requestParams) {
      setRequestParams((prevParams) => ({
        ...prevParams,
        downloadType: initialDownloadType
      }))
    }
  }, [props.requestParams, initialDownloadType])

  // Close modal automatically when download completes
  useEffect(() => {
    if (activeDownloadFormat && !props.downloadLoading) {
      // Download completed, close modal
      props.onClose()
      setActiveDownloadFormat(null)
    }
  }, [props.downloadLoading, activeDownloadFormat])

  const handleDownloadTypeRadio = (value) => {
    setDownloadType(value)
  }

  const handleGetDownload = (format) => {
    setActiveDownloadFormat(format) // Track which format is being downloaded

    const params = {
      ...requestParams,
      documentId: requestParams.documentId ? getMultipleDownloadFileStatementName(requestParams.documentId, downloadType, format) : '',
      downloadType,
      downloadFormat: format
    }

    if (props.fromHome) {
      props.actions.doGetHomeDownload(params)
    } else {
      props.actions.doGetDownload(params)
    }

    props.setDownloadIdFromModal(params.documentId)
  }

  const getDownloadTypeOptions = (ISPFiles) => {
    const options = []
    if (ISPFiles.analyticalPDF || ISPFiles.analyticalXLS) {
      options.push({
        label: intl.formatMessage({ id: 't.app.documents.customDownloadModal.analytical', defaultMessage: 'Analitico' }),
        value: 'A' // Analytical
      })
    }
    if (ISPFiles.synteticPdf) {
      options.push({
        label: intl.formatMessage({ id: 't.app.documents.customDownloadModal.synthetic', defaultMessage: 'Sintetico' }),
        value: 'S' // Synthetic
      })
    }
    return options
  }

  const hasDownloadType = downloadType !== ''
  const isAnalytical = downloadType === 'A'
  const isSynthetic = downloadType === 'S'

  const canDownloadAnalyticalXLS = files.analyticalXLS
  const canDownloadAnalyticalPDF = files.analyticalPDF
  const canDownloadSyntheticPDF = files.synteticPdf

  const getPrimaryButtonConfig = () => {
    if (!hasDownloadType) return { show: false }

    if (isAnalytical) {
      if (canDownloadAnalyticalXLS && canDownloadAnalyticalPDF) {
        return {
          show: true,
          label: props.primaryButtonLabel, // XLS button
          action: () => handleGetDownload('XLS'),
          icon: <DocumentXLS size='20' type='line' />,
          format: 'XLS'
        }
      } else if (canDownloadAnalyticalXLS) {
        return {
          show: true,
          label: props.primaryButtonLabel,
          action: () => handleGetDownload('XLS'),
          icon: <DocumentXLS size='20' type='line' />,
          format: 'XLS'
        }
      } else if (canDownloadAnalyticalPDF) {
        return {
          show: true,
          label: props.secondaryButtonLabel, // PDF button
          action: () => handleGetDownload('PDF'),
          icon: <DocumentPDF size='20' type='line' />,
          format: 'PDF'
        }
      }
    } else if (isSynthetic && canDownloadSyntheticPDF) {
      return {
        show: true,
        label: props.secondaryButtonLabel, // PDF button
        action: () => handleGetDownload('PDF'),
        icon: <DocumentPDF size='20' type='line' />,
        format: 'PDF'
      }
    }

    return { show: false }
  }

  const getSecondaryButtonConfig = () => {
    if (!hasDownloadType) return { show: false }

    if (isAnalytical && canDownloadAnalyticalXLS && canDownloadAnalyticalPDF) {
      return {
        show: true,
        label: props.secondaryButtonLabel, // PDF button
        action: () => handleGetDownload('PDF'),
        icon: <DocumentPDF size='20' type='line' />,
        format: 'PDF'
      }
    }

    return { show: false }
  }

  const primaryButtonConfig = getPrimaryButtonConfig()
  const secondaryButtonConfig = getSecondaryButtonConfig()

  // Determina se ci sono due pulsanti
  const hasTwoButtons = primaryButtonConfig.show && secondaryButtonConfig.show
  const isDownloading = props.downloadLoading && activeDownloadFormat !== null

  return (
    <Toggle>
      <MerchantModal
        title={props.title}
        onClose={props.onClose}
        primaryButtonLabel={primaryButtonConfig.show ? primaryButtonConfig.label : undefined}
        primaryAction={primaryButtonConfig.show ? primaryButtonConfig.action : undefined}
        primaryIcon={primaryButtonConfig.show ? primaryButtonConfig.icon : undefined}
        primaryButtonLoading={activeDownloadFormat === primaryButtonConfig.format && props.downloadLoading}
        primaryButtonDisabled={hasTwoButtons && isDownloading && activeDownloadFormat !== primaryButtonConfig.format}
        secondaryButtonLabel={secondaryButtonConfig.show ? secondaryButtonConfig.label : undefined}
        secondaryAction={secondaryButtonConfig.show ? secondaryButtonConfig.action : undefined}
        secondaryIcon={secondaryButtonConfig.show ? secondaryButtonConfig.icon : undefined}
        secondaryButtonLoading={activeDownloadFormat === secondaryButtonConfig.format && props.downloadLoading}
        secondaryButtonDisabled={hasTwoButtons && isDownloading && activeDownloadFormat !== secondaryButtonConfig.format}
        disableBackdropClick
        primaryButtonTertiary
        secondaryButtonTertiary
      >
        <MerchantGenericCard title={props.description}>
          <div className='documentCustomDownload'>
            <PortalRadioBox
              noPreselection
              radioGroupName='downloadType'
              id='downloadTypeRadio'
              labelTooltip={intl.formatMessage({
                id: 't.app.documents.customDownloadModal.labelTooltip',
                defaultMessage: 'Tipologia estratto conto'
              })}
              row
              defaultValue={downloadType}
              downloadType={downloadType}
              onChange={(value) => handleDownloadTypeRadio(value)}
              options={getDownloadTypeOptions(files)}
              centered
            />
            {props.children}
          </div>
        </MerchantGenericCard>
      </MerchantModal>
    </Toggle>
  )
}

DocumentsStatementsCustomDownloadModal.propTypes = {
  actions: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node,
  primaryButtonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  secondaryButtonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  downloadLoading: PropTypes.bool,
  onClose: PropTypes.func,
  requestParams: PropTypes.object,
  downloadType: PropTypes.string,
  setDownloadIdFromModal: PropTypes.func,
  fromHome: PropTypes.bool
}

DocumentsStatementsCustomDownloadModal.defaultProps = {
  actions: {},
  title: '',
  description: '',
  children: null,
  primaryButtonLabel: '',
  secondaryButtonLabel: '',
  downloadLoading: false,
  onClose: undefined,
  requestParams: {},
  downloadType: '',
  setDownloadIdFromModal: () => { },
  fromHome: false
}

export default DocumentsStatementsCustomDownloadModal
